import React, { useCallback } from 'react';

import { Pagination } from 'antd';
import { GetMonitoringMessagesUpdatesQuery } from 'api/server';
import { createCn } from 'bem-react-classname';
import cn from 'classnames';
import { useFilter, useNav, useUiMemory } from 'hooks';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { useLifecycles } from 'react-use';
import { server } from 'store';
import { Kit } from 'ui/Kit';
import { ProjectLink } from 'ui/ProjectLink';
import { tablePaginationConfig } from 'utils';

import { MonitoringMenu } from '../../shared/MonitoringMenu';

import './MonitoringMessagesUpdatesPage.scss';

interface Props {}

const cx = createCn('MonitoringMessagesUpdatesPage');

export const MonitoringMessagesUpdatesPage: React.FC = () => {
  const dispatch = useDispatch();

  const filter = useFilter<GetMonitoringMessagesUpdatesQuery>({
    timezone: moment.tz.guess(true),
  });

  const getMonitoringMessagesUpdates = useCallback(
    async (query: GetMonitoringMessagesUpdatesQuery, delay?: number) => {
      dispatch(
        server.getMonitoringMessagesUpdates.thunk.request({
          query,
        }),
      );
    },
    [dispatch],
  );

  const getMonitoringMessagesUpdatesState = useSelector(
    server.getMonitoringMessagesUpdates.selector.state,
  );

  const [memoryLimit, setMemoryLimit] = useUiMemory(
    'list.monitoring_updates.nav.limit',
    tablePaginationConfig.defaultPageSize,
  );

  const nav = useNav(getMonitoringMessagesUpdatesState.data?.nav, {
    limit: memoryLimit,
  });

  useLifecycles(
    () => {
      getMonitoringMessagesUpdates({
        ...nav.initialValues,
        ...filter.values,
      });
    },
    () => {
      dispatch(server.getMonitoringMessagesUpdates.action.reset());
    },
  );

  return (
    <div className={cn(cx())}>
      <Kit.LoadContainer
        error={getMonitoringMessagesUpdatesState.error}
        isFetched={getMonitoringMessagesUpdatesState.isFetched}
        isFetching={getMonitoringMessagesUpdatesState.isFetching}
      >
        <MonitoringMenu />
        <Kit.Title>Messages updates monitoring</Kit.Title>

        {getMonitoringMessagesUpdatesState.data && (
          <div className={cx('list')}>
            {getMonitoringMessagesUpdatesState.data.list.map((item) => (
              <div className={cx('item')} key={item.date}>
                <div className={cx('date')}>
                  {moment(item.date).format('DD MMM YYYY')}
                </div>

                <div>
                  {item.details.map((detail) => (
                    <div className={cx('detail')} key={detail.project.id}>
                      <div className={cx('messagesAmount')}>
                        <div className={cx('messagesAmountValue')}>
                          &#183; {detail.messagesAmount}
                        </div>
                      </div>

                      <div className={cx('project')}>
                        {detail.project.name}

                        <ProjectLink
                          to={`/projects/info/${detail.project.id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className={cx('pagination')}>
          <Pagination
            disabled={getMonitoringMessagesUpdatesState.isFetching}
            {...{
              ...tablePaginationConfig,
              ...nav.toAntPagination(),
            }}
            onChange={(current, pageSize) => {
              setMemoryLimit(pageSize as number);

              getMonitoringMessagesUpdates({
                ...filter.values,
                ...nav.fromAntReq({
                  current,
                  pageSize,
                }),
              });
            }}
          />
        </div>
      </Kit.LoadContainer>
    </div>
  );
};
